import { useState } from 'react';
import { Button, Modal, Stack, Text, UnstyledButton } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPuzzle } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { ApiError, unwrap } from '../api/client';
import { puzzleShelf, startPuzzle } from '../api/gen';
import { useOrg } from '../lib/session';

// The Rätsel on the child's start page: a puzzle earned by working a task out
// alone, two a day at most (decision 0006).
//
// It shows only when there is one, so a child who has not earned one is not
// told so. While a session the child just finished is being judged, which
// takes seconds, it says so quietly and asks again.
export function PuzzleCard({ childId, color }: { childId: string; color: string }) {
  const org = useOrg();
  const navigate = useNavigate();
  const [choosing, setChoosing] = useState(false);

  const { data: shelf } = useQuery({
    queryKey: ['puzzles', childId],
    queryFn: () => unwrap(puzzleShelf({ path: { childId }, headers: org })),
    refetchInterval: (q) => ((q.state.data?.judging ?? 0) > 0 ? 4000 : false),
  });

  const start = useMutation({
    mutationFn: (level: number) =>
      unwrap(startPuzzle({ path: { childId }, headers: org, body: { kind: 'sudoku', level } })),
    onSuccess: (p) => p && navigate(`/raetsel/${p.id}`),
    onError: (err) =>
      notifications.show({
        color: 'red',
        message:
          err instanceof ApiError && err.status === 409
            ? 'Heute ist kein Rätsel mehr frei.'
            : (err as Error).message,
      }),
  });

  if (!shelf) return null;
  const open = shelf.open;

  if (!open && shelf.available < 1) {
    return shelf.judging > 0 ? (
      <Text size="sm" c="dimmed" ta="center">
        Sokrates schaut sich dein Gespräch an …
      </Text>
    ) : null;
  }

  return (
    <>
      <Button
        size="md"
        radius="xl"
        color={color}
        // Outlined, so the homework buttons above stay the main way in and
        // the puzzle does not look like one more way to start a sheet.
        variant="outline"
        bg="white"
        leftSection={<IconPuzzle size={20} />}
        onClick={() => (open ? navigate(`/raetsel/${open.id}`) : setChoosing(true))}
      >
        {open ? 'Rätsel weitermachen' : 'Du hast ein Rätsel verdient!'}
      </Button>
      <Modal
        opened={choosing}
        onClose={() => setChoosing(false)}
        title="Wie schwer soll es sein?"
        centered
        radius="lg"
      >
        <Stack gap="sm">
          <Level
            title="Leicht"
            detail="Viele Zahlen stehen schon da"
            disabled={start.isPending}
            onClick={() => start.mutate(0)}
          />
          <Level
            title="Knifflig"
            detail="Nur wenige Zahlen helfen dir"
            disabled={start.isPending}
            onClick={() => start.mutate(1)}
          />
        </Stack>
      </Modal>
    </>
  );
}

function Level(props: { title: string; detail: string; disabled: boolean; onClick: () => void }) {
  return (
    <UnstyledButton
      onClick={props.onClick}
      disabled={props.disabled}
      p="md"
      style={{ borderRadius: 12, border: '1px solid var(--mantine-color-gray-3)' }}
    >
      <Text fw={600}>{props.title}</Text>
      <Text size="sm" c="dimmed">
        {props.detail}
      </Text>
    </UnstyledButton>
  );
}
